/**
 * The flight HUD.
 *
 * ── One console, laid out like a panel ──────────────────────────────────────
 *
 * Left to right: indicator lights, camera controls, heat, the scanner, speed,
 * then the two drive switches. The scanner is dead centre and everything either
 * side of it is grouped by what it is *for* rather than by what it looks like —
 * lamps you glance at, controls you press, gauges you read.
 *
 * The two side halves are declared to the same total width. That is the whole
 * reason the scanner sits in the middle and stays there: with content-sized
 * columns it slid sideways as the gauges changed.
 *
 * ── Nothing moves ───────────────────────────────────────────────────────────
 *
 * Every cell has a fixed size. The captions change constantly — `main` against
 * `precision`, `ready` against `needs 100 speed`, the seat count — and with
 * content-sized boxes each change shoved its neighbours along. A console that
 * rearranges itself while you read it is unreadable, so the widths are declared
 * and the text lives inside them.
 *
 * The lamps say the same words whether lit or dark for the same reason. A lamp
 * whose *label* changes is one you have to read; a lamp that only changes colour
 * is one you can learn the position of and stop reading entirely.
 *
 * ── Orange ──────────────────────────────────────────────────────────────────
 *
 * The chrome matches the reticle and the cursor. The starfield, the target
 * brackets and everything in the world are blues and greens; orange is the
 * colour of *your ship*, so the console, the crosshair and the pointer read as
 * one system and nothing belonging to you disappears into the background.
 *
 * Contacts keep their own colours. They are information about the world rather
 * than part of the ship, and the whole point of a scanner dot is telling a
 * station from a planet at a glance.
 *
 * Pure presentation. Everything here is handed a number by the flight loop and
 * draws it — no state, no timers, so it can be re-rendered as often or as
 * rarely as the loop cares to.
 */

export interface Contact {
  id: string;
  /** Position in the ship's own frame, normalised to the scanner's range. */
  x: number;
  y: number;
  /** Above or below the plane, −1..1. Elite's trick, and it earns its keep. */
  z: number;
  kind: 'star' | 'planet' | 'station' | 'anomaly' | 'ship';
}

const CONTACT_COLORS: Record<Contact['kind'], string> = {
  star: '#ffd479',
  planet: '#7fd4ff',
  station: '#7fb46f',
  anomaly: '#c98bff',
  // Another pilot. Warm, and unlike anything the generator puts in a system —
  // the one contact on the scanner that might do something back.
  ship: '#ff8a3d',
};

import { useEffect, useRef, useState } from 'react';

import { HUD_SCALE, TOUCH_UI, tapProps } from './hudScale';

export const HUD_ORANGE = '#ff8a3d';
export const HUD_TEXT = '#ffd8bd';
export const HUD_DIM = 'rgba(255,216,189,0.45)';
export const HUD_EDGE = 'rgba(255,138,61,0.35)';
export const HUD_BACKING = 'rgba(12, 8, 5, 0.84)';

export const SCANNER = 112;

/**
 * The console's two geometries, and the one sum that holds either together.
 *
 * A phone draws the same console with less air in it. The gaps go from fourteen
 * to five, which is most of fifty points off the width — but the gaps are not
 * free to change, because the scanner sits dead centre only as long as the
 * columns and the gaps add up, and the gaps appear a different number of times
 * on each side of it.
 *
 * Written out, with `C` for the three columns left of the gauges and `B` for the
 * switch bank:
 *
 *     left  = C + heat + 4·gap
 *     right = speed + B + 2·gap
 *
 * The two gauges are the same width, so they cancel, and the whole constraint
 * is:
 *
 *     B = C + 2·gap
 *
 * — which is why the *bank* is what moves when the gap does, and why the gauges
 * could be halved in the last pass without touching any of this. Each geometry
 * below satisfies it, and `console.test.ts` checks every one of them rather than
 * whichever happens to be active, since the tests run on a desktop and the tight
 * one would otherwise never be looked at.
 */
export interface ConsoleGeometry {
  /** Between one instrument and the next. */
  gap: number;
  /** Between the switches inside the bank, which is a tighter thing. */
  switchGap: number;
  /** One switch, and the bank is five of them. */
  switch: number;
}

export const GEOMETRIES: Record<'desktop' | 'touch', ConsoleGeometry> = {
  desktop: { gap: 14, switchGap: 4, switch: 54 },
  /*
   * Tighter, and the switches carry it.
   *
   * `B = C + 2·gap` puts the bank at 268 for a gap of five, and 268 is five
   * switches of 52 with 2 between them. Two points off a switch and two out of
   * each gap inside the bank — the labels still fit, and they are read at 0.65
   * on a display with three device pixels to the point.
   */
  touch: { gap: 5, switchGap: 2, switch: 52 },
};

const GEOMETRY: ConsoleGeometry = TOUCH_UI ? GEOMETRIES.touch : GEOMETRIES.desktop;

/** Space between console columns, in pixels. */
export const CONSOLE_GAP = GEOMETRY.gap;

/**
 * Column widths, in pixels.
 *
 * Chosen so the columns left of the scanner sum to the same width as the ones
 * to its right, *including gaps* — there is one more gap on the right, so the
 * left columns carry an extra gap's worth between them. That arithmetic is the
 * only thing keeping the scanner dead centre, and `consoleColumns` plus its
 * test is what stops the next switch quietly knocking it off.
 *
 * `heat` and `speed` are the same width on purpose now that there are four
 * switches to balance: the two bars flank the scanner, and them matching is
 * both what the eye wants and sixty of the pixels the fourth switch needed.
 */
export const COLUMN = {
  /** Seat and camera. Two buttons, so it needs no more than their labels. */
  camera: 56,
  /** Where you are going: target, bearing, and the way to the chart. */
  navigation: 108,
  lamps: 94,
  /*
   * The gauges, cut from 54 to what they actually hold.
   *
   * Nothing in them was 54 wide: the bar is 12, the throttle mark overhangs it
   * to 22, and the widest thing in the column is the caption underneath — four
   * characters of monospace at ten points with a point of letter-spacing, which
   * is 28. So more than half of each column was blank, twice, either side of the
   * scanner.
   *
   * Thirty is that 28 with a point of air on each side. It is the caption that
   * sets this number, not the bar, so it is the caption — a four-digit speed —
   * that would have to grow before it moves.
   */
  heat: 30,
  speed: 30,
  /**
   * Wide enough for a two-word name at a readable size.
   *
   * From the geometry rather than fixed, because the bank is the column that
   * absorbs a change in the gap — see the note there. The eight pixels over the
   * old 46 are what the tighter `SWITCH_GAP` gives back, so the space came from
   * between the switches rather than from the console.
   */
  switch: GEOMETRY.switch,
} as const;

/** Switches right of the scanner: warp, thrust, jump, gear, cruise. */
export const SWITCH_COUNT = 5;

/**
 * Space between the switches themselves, as against between console columns.
 *
 * Tighter than `CONSOLE_GAP`, because they are one bank of controls rather than
 * five unrelated instruments — and because the space saved is what pays for
 * labels that say "landing gear" instead of "gear". They share a single grid
 * cell for exactly this reason: a grid has one gap, and this needed two.
 */
export const SWITCH_GAP = GEOMETRY.switchGap;

/**
 * The console's columns, left to right, with the scanner in the middle.
 *
 * A function rather than a literal so the balance can be asserted rather than
 * asserted *about* — the widths above are load-bearing and nothing on screen
 * complains when they stop adding up. The scanner simply drifts, and a radar
 * that is not centred on the ship is quietly lying about every bearing on it.
 */
/** The switch bank's width, including the tight gaps inside it. */
export function switchBank(geometry: ConsoleGeometry = GEOMETRY): number {
  return geometry.switch * SWITCH_COUNT + geometry.switchGap * (SWITCH_COUNT - 1);
}

export const SWITCH_BANK = switchBank();

export function consoleColumns(geometry: ConsoleGeometry = GEOMETRY): number[] {
  return [
    // Left to right, by how far from the flying it is. The camera is furthest
    // — it changes nothing about the ship — then where you are *going*, then
    // the conditions the ship is in, then the two gauges either side of the
    // scanner, and finally the switch bank, which is one column holding five.
    COLUMN.camera,
    COLUMN.navigation,
    COLUMN.lamps,
    COLUMN.heat,
    SCANNER,
    COLUMN.speed,
    switchBank(geometry),
  ];
}

/**
 * Gauge height.
 *
 * The bars are the thing you read continuously, so they get the console's full
 * height — everything else is sized around them rather than the other way
 * about. Ninety per cent leaves the caption underneath room to sit without the
 * panel growing to fit it.
 */
const BAR_HEIGHT = Math.round(SCANNER * 0.9);

/** The gap under a gauge, and the caption's line, both of which it stands on. */
const CAPTION_BLOCK = 5 + 12;

/** How far the console sits off the bottom of the screen, before scaling. */
const CONSOLE_BOTTOM = TOUCH_UI ? 6 : 16;

/** The console's own padding — the outside of the panel, not the gaps within. */
const CONSOLE_PAD = TOUCH_UI
  ? { top: 7, side: 6, bottom: 6 }
  : { top: 12, side: 16, bottom: 10 };

/**
 * How far up the screen the console reaches — its top edge, in screen pixels.
 *
 * Exported because the thumb grid has to sit above it and there is nothing in
 * the layout that would make them notice each other: both are absolutely
 * positioned against the bottom, one from the left and one from the middle, and
 * on a phone the console is wide enough that "from the middle" reaches the left
 * corner. They overlapped, and the only sign of it was the pad sitting on the
 * scanner.
 *
 * Derived rather than guessed, from the same numbers the panel is drawn with, so
 * a change to the padding or the scale moves the grid with it. The tallest
 * column is a gauge — bar, gap, caption — rather than the scanner, by six
 * points.
 */
/**
 * How wide the console is drawn, in screen pixels, padding included.
 *
 * Exported for the same reason the clearance is: the thumb grid is pinned to the
 * left edge and the console to the middle, so nothing in the layout makes them
 * notice each other, and on a phone the console is wide enough to reach the
 * corner the grid is in.
 */
export const CONSOLE_WIDTH: number =
  Math.round(
    (consoleColumns().reduce((total, width) => total + width, 0) +
      CONSOLE_GAP * (consoleColumns().length - 1) +
      CONSOLE_PAD.side * 2) *
      HUD_SCALE,
  );

export const CONSOLE_CLEARANCE =
  Math.round(
    (Math.max(SCANNER, BAR_HEIGHT + CAPTION_BLOCK) + CONSOLE_PAD.top + CONSOLE_PAD.bottom) *
      HUD_SCALE,
  ) + CONSOLE_BOTTOM;

/*
 * ── And then measured, because the console is not a fixed height ────────────
 *
 * The number above is what the console is *usually* tall, and it was wrong the
 * moment a route was set: the navigation column grows a line for the next hop,
 * the grid is bottom-anchored, so the whole panel gets taller upward and reaches
 * past the estimate. Which read, from the outside, as the thumb grid sliding
 * down onto the scanner after a jump — the grid had not moved at all.
 *
 * Anything that arranges itself around the console therefore has to watch it
 * rather than predict it. The estimate stays as the value before the first
 * measurement lands, so nothing is positioned from zero on the first frame.
 */
let measured = CONSOLE_CLEARANCE;
const clearanceWatchers = new Set<(px: number) => void>();

function reportClearance(px: number): void {
  if (measured === px) return;
  measured = px;
  for (const watcher of clearanceWatchers) watcher(px);
}

/** The console's top edge, in screen pixels off the bottom of the window. */
export function useConsoleClearance(): number {
  const [clearance, setClearance] = useState(measured);

  useEffect(() => {
    setClearance(measured);
    clearanceWatchers.add(setClearance);
    return () => {
      clearanceWatchers.delete(setClearance);
    };
  }, []);

  return clearance;
}

export interface FlightHudProps {
  speed: number;
  maxSpeed: number;
  /** Commanded speed as a fraction of max, −1..1. */
  throttle: number;
  /** 0..1. */
  heat: number;
  contacts: readonly Contact[];
  /** Whether anything has hold of us. */
  massLock: boolean;
  /** Hull remaining, 0..1. */
  integrity: number;
  /** Whether the autopilot has the ship. */
  cruise: boolean;
  /** What it would fly to if switched on now, or null if there is nothing. */
  cruiseTarget: string | null;
  /** Why it cannot be switched on, when it cannot. */
  cruiseBlocked: string | null;
  onToggleCruise: () => void;
  /** Landing gear. Down to dock, up to warp. */
  gear: boolean;
  onToggleGear: () => void;
  warp: boolean;
  /** Why warp cannot be lit, when it cannot. */
  warpBlocked: string | null;
  onToggleWarp: () => void;
  /** The jump module: system-to-system travel, and the map that goes with it. */
  jump: boolean;
  /** Why it will not come online, when it will not. */
  jumpBlocked: string | null;
  /** How many destinations it is currently showing, for the caption. */
  jumpTargets: number;
  /**
   * What the module is doing, once a jump has been committed to.
   *
   * Overrides the target count and holds the switch lit. Null the rest of the
   * time, which is every moment the module is a thing you are *using* rather
   * than a thing that is running. See `jumpCalibration`.
   */
  jumpStatus?: string | null;
  onToggleJump: () => void;
  precision: boolean;
  /** Whether the hull even has a precision set to switch to. */
  hasPrecision: boolean;
  onTogglePrecision: () => void;
  external: boolean;
  onToggleView: () => void;
  seatIndex: number;
  seatCount: number;
  onNextSeat: () => void;
  /** The hold, on the console beside the camera it shares a column with. */
  hold: { open: boolean; weight: number; capacity: number; urgent: boolean };
  /** The hull, on the console beside the hold it shares a column with. */
  structure: { open: boolean; urgent: boolean };
  onToggleStructure: () => void;
  onToggleHold: () => void;
  /** Where you are going, and which way to look for it. */
  nav: NavReadout;
  onOpenStarmap?: () => void;
  /** The tube, when the ship's power is going. Null when it is off entirely. */
  power?: React.CSSProperties | null;
}

/**
 * What the console says about the trip.
 *
 * Deliberately three short answers rather than a route: *what* you are heading
 * for, the *next* thing you have to reach to get there, and which way to point.
 * A leg-by-leg plan belongs on the chart, where there is room to read it and
 * nothing else competing for your attention.
 */
export interface NavReadout {
  /**
   * Where the ship is: this system, and the cluster holding it.
   *
   * Always present, routed or not. The panel used to show the system name only
   * as a fallback for having nothing else to say, so the moment you set a course
   * the one fact you most want on screen -- where you are -- was replaced by
   * where you were going.
   */
  system: string;
  cluster: string;
  /** The destination, named. Null when nothing is pinned. */
  target: string | null;
  /**
   * What to head for *now*.
   *
   * The same as the target once you are in its system, and the next jump before
   * that. Falls back to the local star with nothing pinned, because a compass
   * that points nowhere is worse than one that points at the only landmark
   * every system is guaranteed to have.
   */
  heading: string;
  /**
   * Bearing to it, in radians, in the ship's own frame. Zero is dead ahead.
   *
   * Null when there is nothing to point at — which should be almost never, and
   * is drawn as a dash rather than as an arrow pointing north by default.
   */
  bearing: number | null;
  /** Whether reaching it means a jump rather than flying there. */
  jump: boolean;
}

export function FlightHud({
  speed,
  maxSpeed,
  throttle,
  heat,
  contacts,
  massLock,
  integrity,
  cruise,
  cruiseTarget,
  cruiseBlocked,
  onToggleCruise,
  gear,
  onToggleGear,
  warp,
  warpBlocked,
  onToggleWarp,
  jump,
  jumpBlocked,
  jumpStatus = null,
  jumpTargets,
  onToggleJump,
  precision,
  hasPrecision,
  onTogglePrecision,
  external,
  onToggleView,
  seatIndex,
  seatCount,
  onNextSeat,
  hold,
  structure,
  onToggleStructure,
  onToggleHold,
  nav,
  onOpenStarmap,
  power,
}: FlightHudProps) {
  const columns = consoleColumns();
  const root = useRef<HTMLDivElement>(null);

  /*
   * `offsetHeight` rather than the observed rect.
   *
   * A `ResizeObserver` reports the layout box, which is what is wanted — but the
   * console carries a transform for the HUD scale and another for the tube
   * collapsing, and a rect measured off the screen would report the panel as
   * three pixels tall halfway through a jump. `offsetHeight` is the untransformed
   * border box, so the scale is applied here, once, deliberately.
   */
  useEffect(() => {
    const element = root.current;
    if (!element) return;

    const measure = () =>
      reportClearance(Math.round(element.offsetHeight * HUD_SCALE) + CONSOLE_BOTTOM);

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={root}
      style={{
        ...power,
        position: 'absolute',
        left: '50%',
        /*
         * Closer to the edge on a phone, and pinned by its own bottom.
         *
         * Scaling about the centre lifts the console off the floor by half of
         * what it shrank — about sixteen points here, on top of the sixteen it
         * was already asking for. Anchoring the scale to the bottom edge instead
         * means this number is the gap you actually get.
         *
         * The tube's collapse then happens about the bottom rather than the
         * centre, which is a difference of a few pixels during a flicker nobody
         * on a phone has seen the other version of.
         */
        bottom: CONSOLE_BOTTOM,
        ...(TOUCH_UI ? { transformOrigin: 'bottom center' } : {}),
        /*
         * Three transforms, composed rather than overwriting one another: the
         * console's own centring, the HUD scale, and the tube's collapse.
         *
         * The order matters. `translateX(-50%)` is outermost so it still moves
         * by half the *unscaled* width — which is exactly what `left: 50%`
         * expects — and the scale applies inside it, about the element's own
         * centre, leaving the console centred at any size.
         */
        transform: `translateX(-50%) scale(${HUD_SCALE}) ${power?.transform ?? ''}`,
        display: 'grid',
        gridTemplateColumns: columns.map((width) => `${width}px`).join(' '),
        gap: CONSOLE_GAP,
        alignItems: 'center',
        /*
         * Trimmed on a phone, and only on the outside.
         *
         * The gaps *between* columns are load-bearing — the widths above are
         * balanced against them to keep the scanner centred, and there is a test
         * that says so — but the console's own padding is symmetric and answers
         * to nothing. Twenty points of width and eight of height for free.
         */
        padding: `${CONSOLE_PAD.top}px ${CONSOLE_PAD.side}px ${CONSOLE_PAD.bottom}px`,
        background: HUD_BACKING,
        border: `1px solid ${HUD_EDGE}`,
        color: HUD_TEXT,
        pointerEvents: 'none',
        /*
         * The switches are held at the same time as the grid is.
         *
         * One finger on an element that refuses gestures is enough to refuse
         * the whole gesture, so this is what stops the browser reading "thumb on
         * roll, finger on the thruster switch" as the beginning of a pinch and
         * cancelling the roll. See the note in `index.html`.
         */
        touchAction: 'none',
      }}
    >
      {/* ── Camera: where you are sitting and what you are looking through ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Button
          label={external ? 'out' : 'seat'}
          onClick={onToggleView}
          title="Recentre the view, then switch camera (V)"
        />
        <Button
          label={seatCount > 0 ? `${seatIndex + 1}/${seatCount}` : '—'}
          enabled={seatCount > 1}
          onClick={onNextSeat}
          title={seatCount > 1 ? 'Move to the next pilot seat (C)' : 'This hull has one seat'}
        />
        {/* The hold. On the console rather than floating over the view, because
            it is a thing the ship has rather than a thing on the screen — and
            amber when something aboard is running out of time, which is the
            whole reason it is visible in flight at all. */}
        <Button
          label="hold"
          on={hold.open}
          colour={hold.urgent ? '#ffb347' : undefined}
          onClick={onToggleHold}
          title={`${hold.weight.toFixed(1)} of ${hold.capacity}w aboard`}
        />
        {/* The ship's own condition, under the hold.
            They answer the same kind of question — what state am I in — and
            neither is about flying, which is what the rest of the console is.
            Four buttons make this column 119px against the scanner's 112, so it
            becomes the tallest thing on the console by a few pixels; anything
            more would start pushing the console up into the docking prompt. */}
        <Button
          label="hull"
          on={structure.open}
          colour={structure.urgent ? '#ff5f6b' : undefined}
          onClick={onToggleStructure}
          title="Which walls are damaged, and how badly"
        />
      </div>

      <NavigationPanel nav={nav} onOpenStarmap={onOpenStarmap} />

      {/* ── Lamps: conditions the ship is in ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {/* Red rather than orange, and the only thing on the console that is.
            Everything else here is a mode; this is a wound. */}
        {/* The number, not just the light. A lamp tells you something is wrong;
            flying a damaged hull is a question of how much is left, and "some"
            is not an answer you can make a decision on. Floored rather than
            rounded, so a scratched hull never reports as a whole one. */}
        <Lamp
          lit={integrity < 1}
          label={`hull ${integrity >= 1 ? 100 : Math.floor(integrity * 100)}%`}
          colour={integrity < 0.4 ? '#ff5f6b' : '#ffb347'}
        />
        <Lamp lit={massLock} label="mass lock" />
        {/* Reverse cannot happen in warp — the throttle cannot command below
            the warp floor — so the two lamps are never lit together. */}
        <Lamp lit={speed < -0.5} label="reverse" />
        <Lamp lit={cruise} label="cruise" />
      </div>

      <Bar
        label="heat"
        fraction={heat}
        // Warns before it matters. A gauge that only speaks once it is at the
        // top has told you too late.
        colour={heat > 0.85 ? '#ff5f6b' : heat > 0.66 ? '#ffb347' : HUD_ORANGE}
      />

      <Scanner contacts={contacts} />

      <Bar
        label={`${Math.round(speed)}`}
        fraction={maxSpeed > 0 ? Math.abs(speed) / maxSpeed : 0}
        colour={warp ? '#ffd479' : HUD_ORANGE}
        marker={Math.abs(throttle)}
        // The bar is unsigned — it shows how fast, not which way — so the mark
        // is the only thing on the gauge that can tell you the throttle is set
        // astern. Blue rather than a shade of the orange, because "am I in
        // reverse" is a yes/no you want to answer without comparing hues.
        markerColour={throttle < 0 ? '#7fd4ff' : HUD_TEXT}
      />

      {/* One bank in one cell, so the switches can sit closer to each other
          than the console's columns do to their neighbours. */}
      <div style={{ display: 'flex', gap: SWITCH_GAP, alignItems: 'flex-start' }}>
      <RockerSwitch
        label="warp drive"
        caption={warp ? 'engaged' : (warpBlocked ?? 'ready')}
        on={warp}
        onClick={onToggleWarp}
        title={warp ? 'Drop out of warp (Y)' : (warpBlocked ?? 'Engage the warp drive (Y)')}
      />
      {/* Up is the main drive. The switch used to read the other way — on meant
          *precision* — which put the console in its thrown position for the
          mode you spend almost no time in, and left it looking switched off
          while the ship was under full power. */}
      <RockerSwitch
        label="thrust toggle"
        caption={precision ? 'precision' : 'main'}
        on={!precision}
        enabled={hasPrecision}
        onClick={onTogglePrecision}
        title={
          hasPrecision
            ? 'Switch between the main drive and the precision thrusters (T)'
            : 'This hull has no precision thrusters'
        }
      />
      {/* Beside warp, because they are the two travel modes and they share a
          gate — main drive, gear stowed. The jump module asks for no speed on
          top of that: plotting is what you do *before* a run, not during. */}
      {/* Once a jump is committed the module stops being a switch and starts
          being an instrument: it reports its own calibration rather than a count
          of places you might go, and it cannot be thrown, because the decision
          has been made. It stays *lit* through all of it — the module is the one
          system doing anything at that point, and showing it dark while it works
          was the thing that read as a fault. */}
      <RockerSwitch
        label="jump module"
        caption={
          jumpStatus ??
          (jump ? (jumpTargets === 1 ? '1 target' : `${jumpTargets} targets`) : (jumpBlocked ?? 'ready'))
        }
        on={jump || jumpStatus !== null}
        enabled={jumpStatus === null}
        onClick={onToggleJump}
        title={
          jumpStatus ??
          (jump
            ? 'Stow the jump module (J)'
            : (jumpBlocked ?? 'Bring up the jump module and its destinations (J)'))
        }
      />
      <RockerSwitch
        label="landing gear"
        caption={gear ? 'down' : 'up'}
        on={gear}
        onClick={onToggleGear}
        title="Raise or lower the landing gear (G). Down to dock, up to warp."
      />
      {/* The autopilot, now a switch among the drive switches. It was a button
          beside the camera on the grounds that it is something you *hand* the
          ship to rather than a mode it is in — true, and it still put the one
          control that flies for you as far from the controls that fly as the
          console allows. */}
      <RockerSwitch
        label="cruise module"
        caption={cruise ? 'engaged' : (cruiseTarget ?? cruiseBlocked ?? 'no target')}
        on={cruise}
        enabled={cruise || cruiseTarget !== null}
        onClick={onToggleCruise}
        title={
          cruise
            ? 'Hand the ship back (R)'
            : cruiseTarget
              ? `Fly to ${cruiseTarget} (R)`
              : `Cruise unavailable: ${cruiseBlocked ?? 'no target'}`
        }
      />
      </div>
    </div>
  );
}

/**
 * Where you are going, on the console.
 *
 * The arrow is the point of it. Names tell you *what* you are looking for and
 * the chart tells you where it is on a map, but neither answers the only
 * question you have while flying — which way do I turn — and that answer has to
 * be somewhere you are already looking.
 */
function NavigationPanel({
  nav,
  onOpenStarmap,
}: {
  nav: NavReadout;
  onOpenStarmap?: () => void;
}) {
  const routed = nav.target !== null;
  /*
   * Two different things, worth two lines.
   *
   * The next jump is what the arrow points at; the destination is where the
   * route ends. They are the same on the last leg and different on every other,
   * and the panel used to show the second as an unlabelled arrow underneath --
   * so a two-hop route read as one name with another name below it and no
   * indication which was which.
   */
  const legName = nav.jump ? nav.heading : null;
  const showsDestination = routed && nav.target !== nav.heading;

  return (
    /* An instrument in its own right, boxed like one.
       Loose in the console it read as three unrelated scraps between the seat
       buttons and the lamps — a word, an arrow and a button that happened to be
       adjacent. A border says they are one thing and answer one question. */
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        minWidth: 0,
        padding: '4px 6px 5px',
        border: `1px solid ${routed ? 'rgba(190,120,255,0.5)' : HUD_EDGE}`,
        background: routed ? 'rgba(190,120,255,0.09)' : 'rgba(255,216,189,0.04)',
      }}
    >
      {/* Where you are, always.
          First line of the instrument rather than a fallback, because it is the
          question a pilot asks most and the one the panel used to stop answering
          the moment a course was set. */}
      <div style={{ ...CAPTION, color: HUD_DIM, textAlign: 'left' }}>system</div>
      <div
        style={{
          fontSize: 11.5,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={nav.cluster ? `${nav.system}, in the ${nav.cluster} cluster` : nav.system}
      >
        {nav.system || '—'}
      </div>
      {nav.cluster && (
        <div
          style={{
            fontSize: 9.5,
            color: HUD_DIM,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={nav.cluster}
        >
          {nav.cluster} cluster
        </div>
      )}

      <div
        style={{
          ...CAPTION,
          color: routed ? ROUTE_VIOLET : HUD_DIM,
          textAlign: 'left',
          marginTop: 4,
          borderTop: `1px solid ${routed ? 'rgba(190,120,255,0.25)' : HUD_EDGE}`,
          paddingTop: 4,
        }}
      >
        {routed ? (legName ? 'next jump' : 'heading') : 'heading'}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        {/* Rotated to the bearing, which is in the ship's own frame with zero
            dead ahead — and `↑` already points that way, so the rotation is the
            bearing with nothing added.

            This was a CSS triangle, on the grounds that a glyph points wherever
            the font decides. True, and it produced a 10px wedge with no shaft:
            at that size there was nothing to say which end was the point. The
            alignment is handled instead of avoided — a fixed square box, centred
            text and `line-height: 1`, so it turns about the middle of a known
            box rather than about a baseline. */}
        <span
          style={{
            flexShrink: 0,
            display: 'inline-block',
            width: 13,
            height: 13,
            lineHeight: '13px',
            textAlign: 'center',
            fontSize: 13,
            color: routed ? ROUTE_VIOLET : HUD_TEXT,
            transform: nav.bearing === null ? undefined : `rotate(${nav.bearing}rad)`,
            opacity: nav.bearing === null ? 0.25 : 1,
          }}
        >
          ↑
        </span>
        <span
          style={{
            fontSize: 11.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={nav.heading}
        >
          {nav.heading}
        </span>
      </div>

      {/* Where the route ends, said as much.
          Only when it differs from the leg -- on the last hop the two are the
          same fact and printing it twice says something has changed when it
          has not. */}
      {showsDestination && (
        <>
          <div style={{ ...CAPTION, color: ROUTE_VIOLET, textAlign: 'left', marginTop: 3 }}>
            destination
          </div>
          <div
            style={{
              fontSize: 11,
              color: HUD_TEXT,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={nav.target ?? undefined}
          >
            {nav.target}
          </div>
        </>
      )}

      {onOpenStarmap && (
        <Button label="starmap" onClick={onOpenStarmap} title="Open the chart" />
      )}
    </div>
  );
}

/** The violet the chart marks a destination in, matched here. */
const ROUTE_VIOLET = '#be78ff';

/**
 * An indicator lamp.
 *
 * A lamp rather than a line of text, because these are conditions the ship is
 * in and you want to register them without reading. The label never changes —
 * only the colour does — so after a few hours you stop reading them at all and
 * just see that the second one is lit.
 *
 * Always drawn, dark when off. A lamp that appears and disappears is a lamp you
 * cannot learn the position of.
 */
function Lamp({
  lit,
  label,
  colour = HUD_ORANGE,
}: {
  lit: boolean;
  label: string;
  colour?: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, height: 14 }}>
      <span
        style={{
          width: 9,
          height: 9,
          flexShrink: 0,
          borderRadius: '50%',
          border: `1px solid ${lit ? colour : 'rgba(255,138,61,0.3)'}`,
          background: lit ? colour : 'rgba(255,138,61,0.07)',
          boxShadow: lit ? `0 0 7px ${colour}` : 'none',
          transition: 'background 90ms linear, box-shadow 90ms linear',
        }}
      />
      <span
        style={{
          fontSize: 9,
          letterSpacing: 0.9,
          textTransform: 'uppercase',
          color: lit ? colour : HUD_DIM,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * A rocker switch.
 *
 * Drawn as a physical thing: a square housing sunk into the panel, with a
 * paddle that sits high when off and low when on, bevelled so the light reads
 * consistently. The `O` and `I` are engraved on the housing behind it, the way
 * they are on a real one.
 *
 * The point is that a control which *changes the ship* should not look like a
 * control which opens a menu. These two throw the drive between modes, and the
 * paddle moving is the feedback — the caption underneath is confirmation, not
 * the signal.
 */
function RockerSwitch({
  label,
  caption,
  on,
  enabled = true,
  onClick,
  title,
}: {
  label: string;
  caption: string;
  on: boolean;
  enabled?: boolean;
  onClick: () => void;
  title: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      {/* Two lines' worth, because the names say what each switch *is* now —
          "landing gear" rather than "gear" — and a fixed single line either
          clipped them or forced the column wider than the console can spare.
          Fixed height regardless, so a one-word switch does not sit lower than
          a two-word one. */}
      <div
        style={{
          ...CAPTION,
          width: COLUMN.switch,
          color: HUD_DIM,
          height: 21,
          fontSize: 9,
          lineHeight: 1.2,
          whiteSpace: 'normal',
        }}
      >
        {label}
      </div>

      <button
        type="button"
        {...tapProps(onClick)}
        disabled={!enabled}
        title={title}
        style={{
          position: 'relative',
          width: COLUMN.switch,
          height: 52,
          padding: 0,
          border: `1px solid ${on ? HUD_ORANGE : HUD_EDGE}`,
          // Sunk into the panel: dark at the top where a recess would be
          // shadowed, lighter at the bottom where it catches the light.
          background: 'linear-gradient(180deg, rgba(4,2,1,0.95), rgba(32,20,12,0.95))',
          boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.75)',
          cursor: enabled ? 'pointer' : 'not-allowed',
          opacity: enabled ? 1 : 0.45,
          overflow: 'hidden',
          pointerEvents: 'auto',
        }}
      >
        {/* Engraved on the housing, behind the paddle. */}
        <span style={{ ...ENGRAVED, top: 3 }}>O</span>
        <span style={{ ...ENGRAVED, bottom: 3 }}>I</span>

        <span
          style={{
            position: 'absolute',
            left: 6,
            right: 6,
            height: 21,
            top: on ? 26 : 4,
            background: on
              ? 'linear-gradient(180deg, #ffb27a, #d2611f)'
              : 'linear-gradient(180deg, #5a4231, #241810)',
            borderTop: `1px solid ${on ? '#ffd8bd' : 'rgba(255,216,189,0.28)'}`,
            borderBottom: '1px solid rgba(0,0,0,0.7)',
            boxShadow: on ? `0 0 8px rgba(255,138,61,0.6)` : '0 2px 3px rgba(0,0,0,0.6)',
            transition: 'top 110ms cubic-bezier(0.3, 0.9, 0.4, 1), background 110ms linear',
          }}
        />
      </button>

      <div
        style={{
          ...CAPTION,
          width: COLUMN.switch,
          height: 22,
          fontSize: 7.5,
          lineHeight: '10px',
          letterSpacing: 0.4,
          overflow: 'hidden',
          color: on ? HUD_ORANGE : HUD_DIM,
        }}
      >
        {caption}
      </div>
    </div>
  );
}

/** A plain panel button, for things that do not change how the ship flies. */
function Button({
  label,
  enabled = true,
  on = false,
  colour,
  onClick,
  title,
}: {
  label: string;
  enabled?: boolean;
  on?: boolean;
  /** Overrides the console amber when the button is saying something. */
  colour?: string;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      {...tapProps(onClick)}
      disabled={!enabled}
      title={title}
      style={{
        width: COLUMN.camera,
        height: 26,
        padding: 0,
        border: `1px solid ${colour ?? (on ? HUD_ORANGE : HUD_EDGE)}`,
        background: on ? 'rgba(255,138,61,0.28)' : 'rgba(20, 12, 7, 0.7)',
        color: colour ?? (on ? '#fff1e4' : enabled ? HUD_TEXT : HUD_DIM),
        font: 'inherit',
        fontSize: 9.5,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        cursor: enabled ? 'pointer' : 'not-allowed',
        opacity: enabled ? 1 : 0.5,
        pointerEvents: 'auto',
      }}
    >
      {label}
    </button>
  );
}

/**
 * A vertical gauge.
 *
 * The speed one carries a second mark for where the throttle is set, against
 * where the ship has got to — the gap between them is the ship still working.
 */
function Bar({
  label,
  fraction,
  colour,
  marker,
  markerColour,
}: {
  label: string;
  fraction: number;
  colour: string;
  marker?: number;
  /** The mark's own colour, when it means something the bar does not. */
  markerColour?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
      <div
        style={{
          width: 12,
          height: BAR_HEIGHT,
          background: 'rgba(255,216,189,0.09)',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: `${Math.min(1, Math.max(0, fraction)) * 100}%`,
            background: colour,
            opacity: 0.9,
          }}
        />
        {marker !== undefined && (
          /* Where the throttle is set, as against where the ship has got to.
             It was a one-pixel hairline the width of the bar, which on a lit
             bar of a similar colour was very nearly invisible — and it is the
             one thing on the console you are actively steering. Now it is a
             three-pixel bar that overhangs both edges, with a dark rim under
             it so it reads against the lit column as well as the empty one. */
          <div
            style={{
              position: 'absolute',
              left: -5,
              right: -5,
              bottom: `${Math.min(1, marker) * 100}%`,
              height: 3,
              marginBottom: -1.5,
              background: markerColour ?? HUD_TEXT,
              boxShadow: `0 0 0 1px rgba(0,0,0,0.65), 0 0 6px ${markerColour ?? HUD_TEXT}`,
            }}
          />
        )}
      </div>
      <div style={{ ...CAPTION, width: '100%' }}>{label}</div>
    </div>
  );
}

/**
 * Proximity, in the ship's own frame.
 *
 * The dot ahead of you is the thing ahead of you — a scanner that needed mental
 * rotation would be no use at the speeds this runs at.
 */
function Scanner({ contacts }: { contacts: readonly Contact[] }) {
  return (
    <div
      style={{
        width: SCANNER,
        height: SCANNER,
        borderRadius: '50%',
        border: `1px solid ${HUD_EDGE}`,
        background: 'rgba(20, 12, 7, 0.6)',
        position: 'relative',
        flexShrink: 0,
      }}
    >
      <div style={ring(0.66)} />
      <div style={ring(0.33)} />
      {/* Nose line, so which way is forward is never in question. */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: 3,
          width: 1,
          height: SCANNER / 2 - 3,
          background: 'rgba(255,138,61,0.28)',
        }}
      />

      {contacts.map((contact) => (
        <div
          key={contact.id}
          title={contact.kind}
          style={{
            position: 'absolute',
            left: `${50 + contact.x * 46}%`,
            top: `${50 - contact.y * 46}%`,
            width: 5,
            height: 5,
            marginLeft: -2.5,
            marginTop: -2.5,
            borderRadius: '50%',
            background: CONTACT_COLORS[contact.kind],
            // Height off the plane fades rather than being drawn as a stalk: at
            // this size a stalk is noise, but "dim means far above or below" is
            // readable at a glance.
            opacity: 1 - Math.min(0.75, Math.abs(contact.z) * 0.75),
          }}
        />
      ))}
    </div>
  );
}

const ring = (scale: number): React.CSSProperties => ({
  position: 'absolute',
  inset: `${(1 - scale) * 50}%`,
  borderRadius: '50%',
  border: '1px solid rgba(255,138,61,0.13)',
});

const ENGRAVED: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  fontSize: 7,
  letterSpacing: 1,
  color: 'rgba(255,216,189,0.3)',
  textAlign: 'center',
  pointerEvents: 'none',
};

const CAPTION: React.CSSProperties = {
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: 1,
  textAlign: 'center',
  fontVariantNumeric: 'tabular-nums',
  color: HUD_DIM,
};
